import { useCallback, useMemo, useState } from 'react';

export type ColumnWidthDef = {
  id: string;
  width: number;
  minWidth?: number;
  maxWidth?: number;
};

function loadStoredWidths(storageKey: string): Record<string, number> | null {
  try {
    const raw = localStorage.getItem(storageKey);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as unknown;
    if (!parsed || typeof parsed !== 'object') return null;
    return parsed as Record<string, number>;
  } catch {
    return null;
  }
}

function saveStoredWidths(storageKey: string, widths: Record<string, number>) {
  try {
    localStorage.setItem(storageKey, JSON.stringify(widths));
  } catch {
    /* storage may be unavailable */
  }
}

export function useColumnWidths(
  columns: ColumnWidthDef[],
  storageKey?: string
): {
  widths: Record<string, number>;
  setWidth: (id: string, width: number) => void;
  resetWidth: (id: string) => void;
  resetAllWidths: () => void;
} {
  const defaults = useMemo(
    () => Object.fromEntries(columns.map((c) => [c.id, c.width])),
    [columns]
  );

  const bounds = useMemo(
    () =>
      Object.fromEntries(
        columns.map((c) => [c.id, { min: c.minWidth ?? 80, max: c.maxWidth ?? 600 }])
      ),
    [columns]
  );

  const [widths, setWidths] = useState<Record<string, number>>(() => {
    const stored = storageKey ? loadStoredWidths(storageKey) : null;
    return { ...defaults, ...(stored ?? {}) };
  });

  const clamp = useCallback(
    (id: string, width: number) => {
      const b = bounds[id];
      if (!b) return width;
      return Math.min(b.max, Math.max(b.min, width));
    },
    [bounds]
  );

  const setWidth = useCallback(
    (id: string, width: number) => {
      setWidths((prev) => {
        const next = { ...prev, [id]: clamp(id, width) };
        if (storageKey) saveStoredWidths(storageKey, next);
        return next;
      });
    },
    [clamp, storageKey]
  );

  const resetWidth = useCallback(
    (id: string) => {
      const defaultWidth = defaults[id];
      if (defaultWidth == null) return;
      setWidth(id, defaultWidth);
    },
    [defaults, setWidth]
  );

  const resetAllWidths = useCallback(() => {
    setWidths(defaults);
    if (storageKey) saveStoredWidths(storageKey, defaults);
  }, [defaults, storageKey]);

  return { widths, setWidth, resetWidth, resetAllWidths };
}
